import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api';
import { updateLeagueSettings, getEffectiveSettings as getEffectiveSettingsApi } from '../adminApi';
import { canViewerSeeLockers } from '../utils/lockVisibilityUtils';

const LeagueSettingsContext = createContext();

export const useLeagueSettings = () => {
  const context = useContext(LeagueSettingsContext);
  if (!context) {
    throw new Error('useLeagueSettings must be used within a LeagueSettingsProvider');
  }
  return context;
};

export const LeagueSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    show_locks: true,
    show_lockers: true,
    allow_duplicates: false,
    allow_future_week_locks: false,
    apply_next_week: false,
    current_mode: 'Full visibility with no duplicates'
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLeagueSettings();
  }, []);

  const fetchLeagueSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.getLeagueSettings();
      setSettings(response.data);
    } catch (err) {
      console.error('Error fetching league settings:', err);
      setError('Failed to load league settings');
    } finally {
      setLoading(false);
    }
  };

  const updateLeagueSettingsContext = async (newSettings) => {
    try {
      setLoading(true);
      setError(null);

      const response = await updateLeagueSettings(newSettings);
      setSettings(response.data.settings);
      return { success: true, message: response.data.message };
    } catch (err) {
      console.error('Error updating league settings:', err);
      const errorMessage = err.response?.data?.error || 'Failed to update league settings';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const getEffectiveSettings = async (weekNumber) => {
    try {
      const response = await getEffectiveSettingsApi(weekNumber);
      return response.data;
    } catch (err) {
      console.error('Error fetching effective settings:', err);
      return settings;
    }
  };

  const canSeeLockers = (weekNumber, userHasLocked, userLocksCount, maxPicks) => {
    void weekNumber;
    return canViewerSeeLockers(settings.show_lockers, userLocksCount, maxPicks);
  };

  const canSeeLocks = () => settings.show_locks;

  const allowDuplicates = () => settings.allow_duplicates;

  const allowFutureWeekLocks = () => settings.allow_future_week_locks;

  const getSettingsDescription = () => settings.current_mode;

  const value = {
    settings,
    loading,
    error,
    fetchLeagueSettings,
    updateLeagueSettings: updateLeagueSettingsContext,
    getEffectiveSettings,
    canSeeLockers,
    canSeeLocks,
    allowDuplicates,
    allowFutureWeekLocks,
    getSettingsDescription
  };

  return (
    <LeagueSettingsContext.Provider value={value}>
      {children}
    </LeagueSettingsContext.Provider>
  );
};
